import { useMemo } from 'react';
import type { HomeDashboardData } from '@/lib/homeDashboard';
import type { Profile } from '@/types/database';

export type UserStage = 'newcomer' | 'scored' | 'dueler' | 'veteran';

/**
 * Lightweight stage derivation from profile fields + optional hints.
 * Use when full HomeDashboardData is not available.
 */
export function useUserStageLight(
  profile: Profile | null,
  hints?: { hasWorkouts?: boolean; duelCount?: number },
): { stage: UserStage } {
  return useMemo(() => {
    if (!profile) return { stage: 'newcomer' as const };

    const hasScore = (profile.duel_standard_strength_kg ?? 0) > 0;
    const hasWorkouts = hints?.hasWorkouts ?? hasScore;
    const duelCount = hints?.duelCount ?? 0;
    const accountAgeDays = profile.created_at
      ? (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
      : 0;

    if (duelCount >= 5 || (accountAgeDays >= 30 && hasScore && duelCount >= 1)) {
      return { stage: 'veteran' as const };
    }
    if (duelCount >= 1) return { stage: 'dueler' as const };
    if (hasScore || hasWorkouts) return { stage: 'scored' as const };
    return { stage: 'newcomer' as const };
  }, [profile, hints?.hasWorkouts, hints?.duelCount]);
}

/**
 * Determines the user's engagement stage for progressive disclosure.
 *
 * Stage 0 "newcomer" — no workouts logged, score is 0
 * Stage 1 "scored"   — has a strength score but no duels yet
 * Stage 2 "dueler"   — has completed at least one duel
 * Stage 3 "veteran"  — 5+ duels completed or active 30+ days
 */
export function useUserStage(
  dashboard: HomeDashboardData | null,
  profile: Profile | null,
): { stage: UserStage } {
  return useMemo(() => {
    if (!dashboard || !profile) return { stage: 'newcomer' as const };

    const score = dashboard.profile?.strength_score ?? 0;
    const hasWorkouts = (dashboard.recent_workouts?.length ?? 0) > 0;
    const hasScore = score > 0;

    // Count completed duels (status === 'completed')
    const allDuels = dashboard.active_duels ?? [];
    const completedDuels = allDuels.filter((d) => d.status === 'completed');
    // active_duels may only contain pending/active; use total length as proxy for experience
    const totalDuelActivity = allDuels.length;

    // Check account age
    const accountAgeDays = profile.created_at
      ? (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
      : 0;

    // Veteran: 5+ duels or 30+ days active with at least some activity
    if (completedDuels.length >= 5 || (accountAgeDays >= 30 && hasScore && totalDuelActivity >= 1)) {
      return { stage: 'veteran' as const };
    }

    // Dueler: at least 1 duel (any status — they've engaged with the duel system)
    if (totalDuelActivity >= 1) {
      return { stage: 'dueler' as const };
    }

    // Scored: has actually logged workouts (not just onboarding baseline data)
    // A score alone from baseline entry isn't enough — they need real workout logs
    if (hasWorkouts) {
      return { stage: 'scored' as const };
    }

    return { stage: 'newcomer' as const };
  }, [dashboard, profile]);
}
